
export type SubscriptionPlan = "free" | "basic" | "premium" | "vip" | "quarterly";
export type GameType = "milhar" | "centena" | "grupo" | "duque" | "terno";
export type PredictionType = "milhar" | "centena" | "duque" | "terno" | "grupo";

// Definir quantos palpites cada plano pode ver
export const getVisibleCount = (plan: SubscriptionPlan, gameType: GameType): number => {
  if (plan === "free") {
    // Usuários gratuitos só veem 2 palpites em milhar e centena
    if (gameType === "milhar" || gameType === "centena") {
      return 2;
    }
    // Outras modalidades são bloqueadas
    return 0;
  }
  
  // Todos os planos pagos veem 10 palpites em todas as modalidades
  return 10;
};

// Verificar se um tipo de jogo está disponível para o plano
export const isGameTypeAvailable = (plan: SubscriptionPlan, gameType: GameType): boolean => {
  if (plan === "free") {
    // Usuários gratuitos só têm acesso a milhar e centena (limitado)
    return gameType === "milhar" || gameType === "centena";
  }
  
  // Todos os planos pagos têm acesso a tudo
  return true;
};

// Verificar se o software turbinado está disponível
export const isSoftwareTurbinadoAvailable = (plan: SubscriptionPlan): boolean => {
  // Software turbinado disponível para planos quarterly, vip e premium
  return plan === "quarterly" || plan === "vip" || plan === "premium";
};

// Export the hasAccessToSoftwareTurbinado function as requested
export const hasAccessToSoftwareTurbinado = (plan: SubscriptionPlan) => {
  return plan === "quarterly" || plan === "premium" || plan === "vip";
};

// Verificar se é usuário admin (acesso total)
export const isAdmin = (role?: string): boolean => {
  return role === "admin";
};

// Função para gerar palpites simulados
export const generatePredictions = (
  gameType: GameType, 
  count: number, 
  aiModel: "gemini" | "chatgpt" | "claude" | "deepseek",
  selectedGroups?: string[]
): Array<{ value: string; animal?: string }> => {
  const predictions: Array<{ value: string; animal?: string }> = [];
  
  // Mapeamento dos grupos para dezenas
  const groupToNumbers: Record<string, number[]> = {
    "01": [1, 2, 3, 4],
    "02": [5, 6, 7, 8],
    "03": [9, 10, 11, 12],
    "04": [13, 14, 15, 16],
    "05": [17, 18, 19, 20],
    "06": [21, 22, 23, 24],
    "07": [25, 26, 27, 28],
    "08": [29, 30, 31, 32],
    "09": [33, 34, 35, 36],
    "10": [37, 38, 39, 40],
    "11": [41, 42, 43, 44],
    "12": [45, 46, 47, 48],
    "13": [49, 50, 51, 52],
    "14": [53, 54, 55, 56],
    "15": [57, 58, 59, 60],
    "16": [61, 62, 63, 64],
    "17": [65, 66, 67, 68],
    "18": [69, 70, 71, 72],
    "19": [73, 74, 75, 76],
    "20": [77, 78, 79, 80],
    "21": [81, 82, 83, 84],
    "22": [85, 86, 87, 88],
    "23": [89, 90, 91, 92],
    "24": [93, 94, 95, 96],
    "25": [97, 98, 99, 0]
  };

  const animalNames: Record<string, string> = {
    "01": "Avestruz", "02": "Águia", "03": "Burro", "04": "Borboleta", "05": "Cachorro",
    "06": "Cabra", "07": "Carneiro", "08": "Camelo", "09": "Cobra", "10": "Coelho",
    "11": "Cavalo", "12": "Elefante", "13": "Galo", "14": "Gato", "15": "Jacaré",
    "16": "Leão", "17": "Macaco", "18": "Porco", "19": "Pavão", "20": "Peru",
    "21": "Touro", "22": "Tigre", "23": "Urso", "24": "Veado", "25": "Vaca"
  };

  // Função para obter números disponíveis baseado nos grupos selecionados
  const getAvailableNumbers = () => {
    if (!selectedGroups || selectedGroups.length === 0) {
      return Object.values(groupToNumbers).flat();
    }
    
    const availableNumbers: number[] = [];
    selectedGroups.forEach(group => {
      if (groupToNumbers[group]) {
        availableNumbers.push(...groupToNumbers[group]);
      }
    });
    return availableNumbers;
  };

  // Função para obter grupos disponíveis
  const getAvailableGroups = () => {
    if (!selectedGroups || selectedGroups.length === 0) {
      return Object.keys(groupToNumbers);
    }
    return selectedGroups;
  };
  
  for (let i = 0; i < count; i++) {
    let value: string;
    let animal: string | undefined;
    
    switch (gameType) {
      case "milhar":
        if (selectedGroups && selectedGroups.length > 0) {
          // Gerar milhar dos grupos específicos
          const availableNumbers = getAvailableNumbers();
          const randomNumber = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
          const firstTwoDigits = Math.floor(Math.random() * 100);
          value = String(firstTwoDigits).padStart(2, "0") + String(randomNumber).padStart(2, "0");
        } else {
          value = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
        }
        break;
      case "centena":
        if (selectedGroups && selectedGroups.length > 0) {
          // Gerar centena dos grupos específicos
          const availableNumbers = getAvailableNumbers();
          const randomNumber = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
          const firstDigit = Math.floor(Math.random() * 10);
          value = String(firstDigit) + String(randomNumber).padStart(2, "0");
        } else {
          value = String(Math.floor(Math.random() * 1000)).padStart(3, "0");
        }
        break;
      case "grupo":
        const availableGroups = getAvailableGroups();
        const randomGroup = availableGroups[Math.floor(Math.random() * availableGroups.length)];
        value = randomGroup;
        animal = animalNames[randomGroup];
        break;
      case "duque":
        if (selectedGroups && selectedGroups.length > 0) {
          // Gerar duque dos grupos específicos
          const availableNumbers = getAvailableNumbers();
          const num1 = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
          const num2 = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
          value = `${String(num1).padStart(2, "0")}-${String(num2).padStart(2, "0")}`;
        } else {
          const num1 = Math.floor(Math.random() * 100);
          const num2 = Math.floor(Math.random() * 100);
          value = `${String(num1).padStart(2, "0")}-${String(num2).padStart(2, "0")}`;
        }
        break;
      case "terno":
        if (selectedGroups && selectedGroups.length > 0) {
          // Gerar terno dos grupos específicos
          const availableNumbers = getAvailableNumbers();
          const num1 = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
          const num2 = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
          const num3 = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
          value = `${String(num1).padStart(2, "0")}-${String(num2).padStart(2, "0")}-${String(num3).padStart(2, "0")}`;
        } else {
          const t1 = Math.floor(Math.random() * 100);
          const t2 = Math.floor(Math.random() * 100);
          const t3 = Math.floor(Math.random() * 100);
          value = `${String(t1).padStart(2, "0")}-${String(t2).padStart(2, "0")}-${String(t3).padStart(2, "0")}`;
        }
        break;
      default:
        value = "0000";
    }
    
    predictions.push({ value, animal });
  }
  
  return predictions;
};

// Função para verificar se o usuário pode gerar palpites
export const canGeneratePredictions = (
  plan: SubscriptionPlan, 
  gameType: GameType,
  role?: string
): boolean => {
  // Admins podem tudo
  if (isAdmin(role)) {
    return true;
  }
  
  // Verificar se o tipo de jogo está disponível para o plano
  return isGameTypeAvailable(plan, gameType);
};

// Função para obter mensagem de upgrade
export const getUpgradeMessage = (gameType: GameType): string => {
  switch (gameType) {
    case "grupo":
      return "Palpites de Grupo disponíveis apenas em planos pagos";
    case "duque":
      return "Palpites de Duque disponíveis apenas em planos pagos";
    case "terno":
      return "Palpites de Terno disponíveis apenas em planos pagos";
    default:
      return "Upgrade para ver todos os palpites";
  }
};
